import { isServiceError } from './errors'

/**
 * Sin conexión, versión mínima (inventario D18 y D39). Lo esencial de Accesos (llave, instrucciones, cómo
 * llegar, clima estático) se guarda en `localStorage` por reserva al leerlo con conexión y se devuelve si
 * la lectura falla por falta de conexión. Las fotos de las instrucciones se guardan con la Cache API, sin
 * service worker: sin conexión se leen como blob URL. Sin almacenamiento o sin CORS en el servidor de las
 * fotos, no se guarda nada y la pantalla muestra el marcador con el texto del paso.
 */
const PREFIX = 'v2.offline.'
const PHOTO_CACHE = 'v2-fotos'

function key(guestCode: string, name: string) {
  return `${PREFIX}${guestCode}.${name}`
}

export function readCached<T>(guestCode: string, name: string): T | null {
  try {
    const raw = localStorage.getItem(key(guestCode, name))
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

export function writeCached(guestCode: string, name: string, value: unknown): void {
  try {
    localStorage.setItem(key(guestCode, name), JSON.stringify(value))
  } catch {
    /* sin almacenamiento */
  }
}

/** Lee con `load`; si responde, lo guarda; si falla por falta de conexión y hay copia, devuelve la copia. */
export async function withOfflineCache<T>(guestCode: string, name: string, load: () => Promise<T>, shouldCache: (value: T) => boolean = () => true): Promise<T> {
  try {
    const value = await load()
    if (shouldCache(value)) writeCached(guestCode, name, value)
    return value
  } catch (e) {
    if (isServiceError(e) && e.code === 'offline') {
      const cached = readCached<T>(guestCode, name)
      if (cached !== null) return cached
    }
    throw e
  }
}

const hasCacheApi = () => typeof caches !== 'undefined'

/** Descarga las fotos a la Cache API. Las que fallan (sin CORS, 404) se ignoran. */
export async function cachePhotos(urls: string[]): Promise<void> {
  if (!hasCacheApi() || urls.length === 0) return
  try {
    const cache = await caches.open(PHOTO_CACHE)
    await Promise.all(
      urls.map(async (url) => {
        if (await cache.match(url)) return
        try {
          const res = await fetch(url, { mode: 'cors' })
          if (res.ok) await cache.put(url, res)
        } catch {
          /* sin CORS o sin red */
        }
      }),
    )
  } catch {
    /* Cache API no disponible (p. ej. contexto no seguro) */
  }
}

/** Blob URL de una foto guardada, o null si no está en la caché. */
export async function cachedPhotoUrl(url: string): Promise<string | null> {
  if (!hasCacheApi()) return null
  try {
    const cache = await caches.open(PHOTO_CACHE)
    const res = await cache.match(url)
    return res ? URL.createObjectURL(await res.blob()) : null
  } catch {
    return null
  }
}
